import { DivergentRouteContext, DivergentRouteDecision } from './types';
import { Order, SystemConfig } from '../../../../types';

/**
 * ESTRATÉGIA DESPACHO RETIDO
 * 
 * Conceito: Segurar TODO o cluster (incluindo pedidos prontos) aguardando
 * os pedidos em produção ficarem prontos, para sair tudo junto em uma única rota.
 * 
 * Quando usar vs Bumerangue:
 * - Bumerangue: Usado quando há MIX de prontos/não-prontos E o slack permite 
 *   uma ida+volta sem atrasar ninguém
 * - Despacho Retido: Usado quando NÃO faz sentido fazer bumerangue, ou seja,
 *   quando o tempo de espera pelos não-prontos é curto o suficiente para que
 *   os prontos não atrasem, mas longo o suficiente para evitar uma viagem extra
 * 
 * VIABILIDADE (critérios que devem ser atendidos):
 * 
 * C1 - Tempo de produção não compromete o último pedido:
 *    max_tempo_producao + tempo_rota_total <= slack_do_pedido_mais_critico
 * 
 * C2 - Não é mais vantajoso fazer bumerangue:
 *    Se bumerangue é viável, ele tem prioridade (economia de tempo)
 *    Despacho retido é "plano B" quando bumerangue não cabe
 * 
 * C3 - Ganho operacional vs duas rotas separadas:
 *    Rota consolidada: 1 ida + todas entregas + 1 volta ao hub
 *    Duas rotas separadas: 2 idas + entregas + 2 voltas ao hub
 *    Ganho: economiza 1 deslocamento completo (ida+volta extra)
 *    Além disso: evita custos fixos duplicados (setup, retorno ao hub)
 * 
 * @param context - Dados completos da rota
 * @returns Decisão de retenção do cluster completo
 */
export const createDispatchHoldDecision = (context: DivergentRouteContext): DivergentRouteDecision | null => {
    const cluster = context.cluster;
    const readyOrders = cluster.filter((order) => order.isReady || order.status === 'ready');
    const notReadyOrders = cluster.filter((order) => !order.isReady && order.status !== 'ready');
    
    // Se não houver pedidos não-prontos, não faz sentido reter
    if (notReadyOrders.length === 0) return null;
    
    // Se todos estão não-prontos, é uma retenção padrão (aguardando cozinha)
    // Se há mix, precisamos avaliar se vale a pena segurar os prontos

    // CALCULA TEMPOS
    const firstLeg = context.firstLegTiming;
    const secondLeg = context.secondLegTiming;
    const returnTime = context.returnMins;
    
    // Margem de segurança
    const safetyMargin = Math.max(2, (context.config.fleetConfig.slaToleranceMinutes || 10) / 3);

    // Estima tempo máximo de produção restante
    // Heurística: assume que pedidos não-prontos levam em média 60% do tempo de cozinha
    const avgKitchenTime = context.config.averageKitchenTime || 20;
    const estimatedProductionTime = notReadyOrders.length > 0 
        ? Math.ceil(avgKitchenTime * 0.6) // 60% do tempo médio como estimativa conservadora
        : 0;

    // VALIDAÇÃO C1: Quando os não-prontos ficarem prontos, a rota completa cabe no slack?
    // Calcula o slack mínimo considerando o tempo de espera + execução
    const minSlackInCluster = Math.min(...cluster.map(o => calculateOrderSlack(o, context.config)));
    
    // Tempo total da rota quando todos estiverem prontos
    // Se temos firstLeg e secondLeg, combinamos; senão, usa o timing da rota completa
    let totalRouteTime: number;
    if (firstLeg && secondLeg) {
        // Rota consolidada = primeira leva + segunda leva + volta
        totalRouteTime = firstLeg.totalMinutes + secondLeg.totalMinutes + returnTime;
    } else {
        // Fallback: usa o internalCashCost como proxy (já inclui tempos)
        // Ou calcula estimativa baseada na distância média
        const avgTravelPerOrder = 8; // minutos estimados
        totalRouteTime = (avgTravelPerOrder * cluster.length) + 
                        (context.serviceTimePerOrder * cluster.length) + 
                        returnTime;
    }
    
    // Tempo total até completar = espera produção + execução rota
    const totalTimeUntilCompletion = estimatedProductionTime + totalRouteTime;
    
    const c1_fitsInSlack = totalTimeUntilCompletion <= (minSlackInCluster - safetyMargin);

    // VALIDAÇÃO C2: Checa se Bumerangue seria melhor
    // Se o slack é grande o suficiente para bumerangue, ele tem prioridade
    // Despacho retido só entra quando bumerangue falha
    const boomerangWouldBeBetter = context.minSlack > Math.max(6, context.config.fleetConfig.slaToleranceMinutes || 10);
    
    // Se bumerangue é viável, não fazemos despacho retido (ele é "plano B")
    // Esta validação é indireta - o código em smartSplit.ts já garante a ordem
    // if (boomerangWouldBeBetter) return null; // Descomente se quiser forçar prioridade estrita

    // VALIDAÇÃO C3: Ganho operacional
    // Rota consolidada: 1 deslocamento (ida) + entregas + 1 volta
    // Duas rotas separadas: 2 deslocamentos (ida1 + ida2) + entregas + 2 voltas
    // Ganho real: economia de 1 deslocamento completo (ida+volta)
    const consolidatedRouteTime = totalRouteTime; // já calculado acima
    const twoSeparateRoutesTime = (firstLeg?.totalMinutes || 0) + returnTime + 
                                   (secondLeg?.totalMinutes || 0) + returnTime; // ida1+volta1 + ida2+volta2
    
    // Se a rota consolidada economiza pelo menos um deslocamento (ida+volta), é vantajosa
    const timeSaved = twoSeparateRoutesTime - consolidatedRouteTime;
    const c3_operationalGain = timeSaved > (returnTime * 0.5); // Economiza pelo menos metade de um ciclo completo

    // LOG DE DECISÃO
    const decisionLog = {
        c1_fitsInSlack,
        c3_operationalGain,
        timeSaved,
        estimatedProductionTime,
        totalRouteTime,
        consolidatedRouteTime,
        twoSeparateRoutesTime,
        totalTimeUntilCompletion,
        minSlackInCluster,
        safetyMargin,
        readyCount: readyOrders.length,
        notReadyCount: notReadyOrders.length,
        boomerangWouldBeBetter
    };

    // Se não cabe no slack, não retemos (melhor despachar o que está pronto)
    if (!c1_fitsInSlack) {
        return null;
    }

    // ESTRATÉGIA VIÁVEL - Retém todo o cluster
    return {
        heldOrders: cluster, // TODOS os pedidos são retidos
        trace: {
            kind: 'strategy',
            mode: 'hold',
            decisionReason: 'RETENTION_STRATEGY',
            orderIds: cluster.map((order) => order.id),
            chosenLabel: 'DISPATCH_HOLD',
            rationale: `Despacho retido: ${notReadyOrders.length} pedidos em produção. ` +
                      `Estimativa: ${estimatedProductionTime}min de cozinha. ` +
                      `Rota consolidada: ${totalTimeUntilCompletion.toFixed(1)}min ` +
                      `(slack: ${minSlackInCluster.toFixed(1)}min). ` +
                      `${readyOrders.length} prontos aguardam para saída conjunta.`,
            ownCost: context.internalCashCost,
            thirdCost: context.thirdPartyCost,
            slackMin: context.minSlack,
            constraints: ['dispatch_hold', 'fleet_profile', 'sla_tolerance_route', 'production_wait', 'consolidated_route']
        },
        decisionRationale: JSON.stringify(decisionLog)
    };
};

/**
 * Helper: Calcula o slack disponível para um pedido específico
 * Replica a lógica do sistema principal de safe dispatch
 * 
 * IMPORTANTE: NÃO descontamos o tempo de produção (kitchenTime) do slack,
 * pois a produção acontece em paralelo ao deslocamento. O entregador
 * deve sair baseado apenas no tempo de entrega (deslocamento + serviço).
 */
function calculateOrderSlack(order: Order, config: SystemConfig): number {
    const elapsedMins = (Date.now() - (order.createdAt || Date.now())) / 60000;
    const timeLimit = order.timeLimit || 60;
    const travelTime = (order.estimatedDuration || 10);
    const serviceTime = config.fleetConfig.serviceTimeMinutes || 3;
    const tolerance = config.fleetConfig.slaToleranceMinutes || 10;
    
    // Slack = Deadline - Elapsed - Travel - Service + Tolerance
    // NÃO inclui kitchenTime (tempo de produção)
    return Math.max(0, timeLimit - elapsedMins - travelTime - serviceTime + tolerance);
}
